import { computed, ref } from 'vue'
import { getOrderTypesAction, type OrderTypesFilters } from '@/api/functions/services/get-order-types.action'
import {
  getServiceCategoriesAction,
  type ServiceCategoriesFilters,
} from '@/api/functions/services/get-service-categories.action'
import {
  getServiceRegionsAction,
  type ServiceRegion,
  type ServiceRegionOption,
} from '@/api/functions/services/get-service-regions.action'
import { getServiceTypesAction, type ServiceTypesFilters } from '@/api/functions/services/get-service-types.action'
import { getServicesAction } from '@/api/functions/services/get-services.action'
import {
  getServiceProvidersAction,
  type ServiceProvider,
  type ServiceProviderOption,
} from '@/api/functions/services/providers/get-service-providers.action'
import type { Service, ServicesFilters } from '@/api/typings/service.typing'
import { useQueryApi } from './useApi'

export function useServices(initialFilters: ServicesFilters = {}) {
  const currentPage = ref(initialFilters.page || 1)
  const activeFilters = ref<ServicesFilters>({ ...initialFilters })

  const query = useQueryApi({
    queryKey: computed(() => [
      'services',
      {
        ...activeFilters.value,
        page: currentPage.value,
      },
    ]),
    queryFn: () =>
      getServicesAction({
        ...activeFilters.value,
        page: currentPage.value,
      }),
    staleTime: 1000 * 60 * 3,
  })

  const services = computed<Service[]>(() => query.data.value?.data || [])
  const pagination = computed(() => query.data.value?.pagination)
  const message = computed(() => (query.data.value as any)?.meta?.message)

  const nextPage = () => {
    if (pagination.value?.hasNextPage) currentPage.value++
  }

  const prevPage = () => {
    if (currentPage.value > 1) currentPage.value--
  }

  const goToPage = (page: number) => {
    currentPage.value = page
  }

  const updateFilters = (newFilters: ServicesFilters) => {
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    )

    activeFilters.value = { ...cleanFilters }
    currentPage.value = 1
  }

  const refetchServices = async () => {
    await query.refetch()
  }

  return {
    services,
    pagination,
    message,
    currentPage,
    activeFilters,

    nextPage,
    prevPage,
    goToPage,
    updateFilters,
    refetchServices,

    ...query,
  }
}

export function useServiceCategories(initialFilters: ServiceCategoriesFilters = {}) {
  const currentPage = ref(initialFilters.page ?? 1)
  const activeFilters = ref<ServiceCategoriesFilters>({
    ...initialFilters,
  })

  const query = useQueryApi({
    queryKey: computed(() => [
      'service-categories',
      {
        ...activeFilters.value,
        page: currentPage.value,
      },
    ]),
    queryFn: () =>
      getServiceCategoriesAction({
        ...activeFilters.value,
        page: currentPage.value,
      }),
    staleTime: 1000 * 60 * 3,
  })

  const categories = computed(() => query.data.value?.data ?? [])

  const categoryOptions = computed(() =>
    categories.value.map((category) => ({
      label: category.name,
      value: category.id,
      ...category,
    }))
  )

  const pagination = computed(() => query.data.value?.pagination)
  const message = computed(() => query.data.value?.meta?.message)

  const nextPage = () => {
    if (
      pagination.value?.currentPage &&
      pagination.value?.lastPage &&
      pagination.value.currentPage < pagination.value.lastPage
    ) {
      currentPage.value++
    }
  }

  const prevPage = () => {
    if (currentPage.value > 1) {
      currentPage.value--
    }
  }

  const goToPage = (page: number) => {
    currentPage.value = page
  }

  const updateFilters = (newFilters: ServiceCategoriesFilters) => {
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    )

    activeFilters.value = { ...cleanFilters }
    currentPage.value = 1
  }

  const refetchCategories = async () => {
    await query.refetch()
  }

  return {
    categories,
    categoryOptions,
    pagination,
    message,
    currentPage,
    activeFilters,
    nextPage,
    prevPage,
    goToPage,
    updateFilters,
    refetchCategories,
    ...query,
  }
}

export function useServiceTypes(initialFilters: ServiceTypesFilters = {}) {
  const currentPage = ref(initialFilters.page ?? 1)
  const activeFilters = ref<ServiceTypesFilters>({
    ...initialFilters,
  })

  const query = useQueryApi({
    queryKey: computed(() => [
      'service-types',
      {
        ...activeFilters.value,
        page: currentPage.value,
      },
    ]),
    queryFn: () =>
      getServiceTypesAction({
        ...activeFilters.value,
        page: currentPage.value,
      }),
    staleTime: 1000 * 60 * 3,
  })

  const serviceTypes = computed(() => query.data.value?.data ?? [])

  const serviceTypeOptions = computed(() =>
    serviceTypes.value.map((serviceType) => ({
      label: serviceType.name,
      value: serviceType.id,
      ...serviceType,
    }))
  )

  const pagination = computed(() => query.data.value?.pagination)
  const message = computed(() => query.data.value?.meta?.message)

  const nextPage = () => {
    if (
      pagination.value?.currentPage &&
      pagination.value?.lastPage &&
      pagination.value.currentPage < pagination.value.lastPage
    ) {
      currentPage.value++
    }
  }

  const prevPage = () => {
    if (currentPage.value > 1) {
      currentPage.value--
    }
  }

  const goToPage = (page: number) => {
    currentPage.value = page
  }

  const updateFilters = (newFilters: ServiceTypesFilters) => {
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    )

    activeFilters.value = { ...cleanFilters }
    currentPage.value = 1
  }

  const refetchServiceTypes = async () => {
    await query.refetch()
  }

  return {
    serviceTypes,
    serviceTypeOptions,
    pagination,
    message,
    currentPage,
    activeFilters,
    nextPage,
    prevPage,
    goToPage,
    updateFilters,
    refetchServiceTypes,
    ...query,
  }
}

export function useOrderTypes(initialFilters: OrderTypesFilters = {}) {
  const currentPage = ref(initialFilters.page ?? 1)
  const activeFilters = ref<OrderTypesFilters>({
    ...initialFilters,
  })

  const query = useQueryApi({
    queryKey: computed(() => [
      'order-types',
      {
        ...activeFilters.value,
        page: currentPage.value,
      },
    ]),
    queryFn: () =>
      getOrderTypesAction({
        ...activeFilters.value,
        page: currentPage.value,
      }),
    staleTime: 1000 * 60 * 3,
  })

  const orderTypes = computed(() => query.data.value?.data ?? [])

  const orderTypeOptions = computed(() =>
    orderTypes.value.map((orderType) => ({
      label: orderType.name,
      value: orderType.id,
      ...orderType,
    }))
  )

  const pagination = computed(() => query.data.value?.pagination)

  const message = computed(() => query.data.value?.meta?.message)

  const nextPage = () => {
    if (pagination.value && currentPage.value < pagination.value.lastPage) {
      currentPage.value++
    }
  }

  const prevPage = () => {
    if (currentPage.value > 1) {
      currentPage.value--
    }
  }

  const goToPage = (page: number) => {
    currentPage.value = page
  }

  const updateFilters = (newFilters: OrderTypesFilters) => {
    const cleanFilters = Object.fromEntries(
      Object.entries(newFilters).filter(([, value]) => value !== '' && value !== null && value !== undefined)
    )

    activeFilters.value = { ...cleanFilters }
    currentPage.value = 1
  }

  const refetchOrderTypes = async () => {
    await query.refetch()
  }

  return {
    orderTypes,
    orderTypeOptions,
    pagination,
    message,
    currentPage,
    activeFilters,
    nextPage,
    prevPage,
    goToPage,
    updateFilters,
    refetchOrderTypes,
    ...query,
  }
}

export function useServiceProviders() {
  const query = useQueryApi({
    queryKey: ['service-providers'],
    queryFn: async () => {
      const firstPage = await getServiceProvidersAction({ page: 1 })

      const total = firstPage.pagination?.total ?? 0
      const perPage = firstPage.pagination?.perPage ?? 0
      const lastPage = firstPage.pagination?.lastPage ?? (perPage > 0 ? Math.ceil(total / perPage) : 1)

      if (lastPage <= 1) {
        return firstPage
      }

      const remainingPages = await Promise.all(
        Array.from({ length: lastPage - 1 }, (_, index) => getServiceProvidersAction({ page: index + 2 }))
      )

      return {
        ...firstPage,
        data: [firstPage, ...remainingPages].flatMap((response) => response.data ?? []),
      }
    },
  })

  const serviceProviders = computed<ServiceProvider[]>(() => query.data.value?.data ?? [])

  const providerOptions = computed<ServiceProviderOption[]>(() =>
    serviceProviders.value.map((provider) => ({
      ...provider,
      label: provider.name,
      value: provider.id,
    }))
  )

  return {
    ...query,
    serviceProviders,
    providerOptions,
  }
}
export function useServiceRegions() {
  const query = useQueryApi({
    queryKey: ['service-regions'],
    queryFn: () => getServiceRegionsAction(),
  })

  const serviceRegions = computed<ServiceRegion[]>(() => query.data.value?.data ?? [])

  const regionOptions = computed<ServiceRegionOption[]>(() =>
    serviceRegions.value.map((region) => ({
      ...region,
      label: region.name,
      value: region.id,
    }))
  )

  return {
    ...query,
    serviceRegions,
    regionOptions,
  }
}
